import React, { useState, useCallback, useMemo } from "react";
import cn from "classnames";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import SearchResults from "react-filter-search";
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { analytic } from "../../firebase";
import GlobalState from "../../useGlobalState";
import ExternalDraggableNode from "../external-draggable-node/ExternalDraggableNode";
import AttributesTabContent from "./AttributesTabContent";

import layoutData from "../../data/classes-list/layout.json";
import flexboxData from "../../data/classes-list/flexbox.json";
import gridData from "../../data/classes-list/grid.json";
import spacingData from "../../data/classes-list/spacing.json";
import sizingData from "../../data/classes-list/sizing.json";
import typographyData from "../../data/classes-list/typography.json";
import backgroundsData from "../../data/classes-list/backgrounds.json";
import bordersData from "../../data/classes-list/borders.json";
import tablesData from "../../data/classes-list/tables.json";
import effectsData from "../../data/classes-list/effects.json";
import transitionsAnimationsData from "../../data/classes-list/transition-animation.json";
import transformsData from "../../data/classes-list/tranforms.json";
import interactivityData from "../../data/classes-list/interactivity.json";
import svgData from "../../data/classes-list/svg.json";
import accessibilityData from "../../data/classes-list/accessibility.json";

import htmlTagList from "../../data/html-tag-list.json";

const ResponsiveClassToggler = ({ activeClasses, classToToggle }) => {
  const { dispatch } = GlobalState.useContainer();
  const breakpoints = ["sm", "md", "lg", "xl"];

  return (
    <ul className="flex space-x-4">
      {breakpoints.map((bp) => {
        const isClassEnabled =
          activeClasses && activeClasses.includes(`${bp}:${classToToggle}`);
        return (
          <li
            key={`${bp}:${classToToggle}`}
            className={cn(
              {
                "bg-green-600 text-white": isClassEnabled,
                "bg-white text-gray-900": !isClassEnabled,
              },
              "rounded-full shadow p-4 cursor-pointer"
            )}
            onClick={() => {
              dispatch({
                type: "CHANGE_NODE_CLASS_UNDER_TREE_DATA_AT_PATH",
                payload: {
                  _class: `${bp}:${classToToggle}`,
                },
              });
            }}
          >
            {bp}
          </li>
        );
      })}
    </ul>
  );
};

const RenderCheckbox = (classesList = []) => {
  const { state, dispatch } = GlobalState.useContainer();
  const [filterTerm, setFilterTerm] = useState("");

  const selectedNodeId = state.selectedNode?.id;

  const activeClasses =
    (state.attributes && state.attributes[selectedNodeId]?.classes) || [];

  const handleChange = (event) => {
    const { value } = event.target;
    setFilterTerm(value);
  };
  return useMemo(
    () => (
      <div className="flex-grow h-full p-2">
        <input
          placeholder="Search a class"
          className="bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-lg py-2 px-4 block w-full appearance-none leading-normal"
          type="text"
          value={filterTerm}
          onChange={handleChange}
        />
        <div className="flex flex-wrap h-full">
          {selectedNodeId && (
            <SearchResults
              value={filterTerm}
              data={classesList}
              renderResults={(results) => (
                <AutoSizer>
                  {({ height, width }) => {
                    const numberOfColumn = Math.floor(width / (250 * 1));
                    const colWidth = width / numberOfColumn;
                    const colHeight = colWidth / 0.66;

                    const rowCount = Math.ceil(results.length / numberOfColumn);

                    return (
                      <Grid
                        columnCount={numberOfColumn}
                        columnWidth={colWidth}
                        height={height}
                        rowCount={rowCount}
                        rowHeight={145}
                        width={width}
                      >
                        {({ columnIndex, rowIndex, style }) => {
                          const _class =
                            results[rowIndex * numberOfColumn + columnIndex]
                              ?.name;
                          if (!_class) return null;
                          const isClassEnabled = activeClasses.includes(_class);

                          return (
                            <div style={style} key={_class}>
                              <div
                                className={cn(
                                  // {
                                  //   "bg-green-600 text-white": isClassEnabled,
                                  //   "bg-white text-gray-900": !isClassEnabled,
                                  // },
                                  "m-2 flex flex-col items-center bg-white rounded-lg shadow  font-mono"
                                )}
                              >
                                <div
                                  className={cn(
                                    {
                                      "bg-green-600 text-white": isClassEnabled,
                                      "bg-white text-gray-900": !isClassEnabled,
                                    },
                                    "cursor-pointer shadow rounded-full p-4 m-2"
                                  )}
                                  onClick={() => {
                                    if (analytic) {
                                      analytic.logEvent("toggle_tw_class", {
                                        name: _class,
                                      });
                                    }
                                    dispatch({
                                      type:
                                        "CHANGE_NODE_CLASS_UNDER_TREE_DATA_AT_PATH",
                                      payload: {
                                        _class,
                                      },
                                    });
                                  }}
                                >
                                  <h4 className="text-md  leading-tight ">
                                    <span>.{_class}</span>
                                  </h4>
                                </div>
                                <div className="m-2">
                                  <ResponsiveClassToggler
                                    activeClasses={activeClasses}
                                    classToToggle={_class}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      </Grid>
                    );
                  }}
                </AutoSizer>
              )}
            />
          )}
        </div>
      </div>
    ),
    [activeClasses, selectedNodeId, filterTerm]
  );
};

const allData = [
  layoutData,
  flexboxData,
  gridData,
  spacingData,
  sizingData,
  typographyData,
  backgroundsData,
  bordersData,
  tablesData,
  effectsData,
  transitionsAnimationsData,
  transformsData,
  interactivityData,
  svgData,
  accessibilityData,
];

const allClasses = allData
  .flatMap((x) => x.subcategories)
  .flatMap((x) => x.classes);
const CustomTab = ({ data }) => {
  return (
    <Tabs>
      <TabList>
        {data.subcategories.map((subcategory) => {
          return (
            <Tab
              onClick={() => {
                if (analytic) {
                  analytic.logEvent("select_subcategory_tab", {
                    name: subcategory.name,
                  });
                }
              }}
              key={subcategory.name}
            >
              {subcategory.name}
            </Tab>
          );
        })}
      </TabList>
      {data.subcategories.map((subcategory) => {
        return (
          <TabPanel key={subcategory.name}>
            <div className="flex flex-wrap h-full">
              {RenderCheckbox(subcategory.classes)}
            </div>
          </TabPanel>
        );
      })}
    </Tabs>
  );
};

const ClassesCategories = [
  "All",
  "Layout",
  "Flexbox",
  "Grid",
  "Spacing",
  "Sizing",
  "Typography",
  "Backgrounds",
  "Borders",
  "Tables",
  "Effects",
  "Transitions/Animations",
  "Transforms",
  "Interactivity",
  "SVG",
  "Accessibility",
];

const ClassesTab = () => {
  return (
    <Tabs>
      <TabList>
        {ClassesCategories.map((category) => (
          <Tab
            key={category}
            onClick={() => {
              if (analytic) {
                analytic.logEvent("select_category_tab", {
                  name: category,
                });
              }
            }}
          >
            {category}
          </Tab>
        ))}
      </TabList>
      <TabPanel>{RenderCheckbox(allClasses)}</TabPanel>
      <TabPanel>
        <CustomTab data={layoutData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={flexboxData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={gridData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={spacingData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={sizingData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={typographyData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={backgroundsData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={bordersData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={tablesData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={effectsData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={transitionsAnimationsData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={transformsData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={interactivityData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={svgData} />
      </TabPanel>
      <TabPanel>
        <CustomTab data={accessibilityData} />
      </TabPanel>
    </Tabs>
  );
};

const HtmlTagListTab = React.memo(() => {
  const [filterTerm, setFilterTerm] = useState("");
  const handleChange = (event) => {
    const { value } = event.target;
    setFilterTerm(value);
  };
  return (
    <div>
      <input
        placeholder="Search a class"
        className="bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-lg py-2 px-4 block w-full appearance-none leading-normal"
        type="text"
        value={filterTerm}
        onChange={handleChange}
      />
      <div className="grid grid-cols-6">
        {
          <SearchResults
            value={filterTerm}
            data={htmlTagList}
            renderResults={(results) =>
              results.map((htmlTag) => {
                const tag = htmlTag.tag;

                return (
                  <div key={tag}>
                    <ExternalDraggableNode
                      node={{
                        title: tag,
                        subtitle: tag,
                        tag: tag,
                        customHtmlAvailable: htmlTag.customHtmlAvailable,
                        childrenLess: htmlTag.childrenLess,
                      }}
                    />
                  </div>
                );
              })
            }
          />
        }
      </div>
    </div>
  );
});

const SingleActiveClass = ({ _class }) => {
  const { dispatch } = GlobalState.useContainer();
  const onClickHandler = useCallback(() => {
    dispatch({
      type: "CHANGE_NODE_CLASS_UNDER_TREE_DATA_AT_PATH",
      payload: {
        _class,
      },
    });
  }, [_class, dispatch]);

  return (
    <div class="flex flex-shrink-0 bg-gray-200 rounded-full px-3 py-1 text-sm font-semibold text-gray-700 mr-2">
      {_class}
      <span
        className=" cursor-pointer bg-red-700 rounded-full text-white p-1 mx-1"
        onClick={onClickHandler}
      >
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </span>
    </div>
  );
};

const ActiveClassesBar = ({ classes }) => {
  return (
    <div className="flex p-1 mb-1">
      <div className="">
        <span>Active Classes :</span>
      </div>
      <div className="flex flex-no-wrap overflow-auto">
        {classes &&
          classes.map((_class) => <SingleActiveClass _class={_class} />)}
      </div>
    </div>
  );
};
function ConfigPanel() {
  const { state } = GlobalState.useContainer();

  const selectedNodeId = state.selectedNode?.id;

  const activeClasses =
    (state.attributes && state.attributes[selectedNodeId]?.classes) || [];

  const isCustomHtmlAvailable = state.selectedNode?.customHtmlAvailable;

  return useMemo(
    () => (
      <>
        <div className="flex flex-col h-full border-2 p-2">
          <div className="overflow-y-scroll">
            <ActiveClassesBar classes={activeClasses} />
            <Tabs>
              <TabList>
                <Tab>Classes</Tab>
                <Tab>Elements</Tab>
                {isCustomHtmlAvailable && <Tab>Custom Html</Tab>}
              </TabList>

              <TabPanel>
                <ClassesTab />
              </TabPanel>
              <TabPanel forceRender={true}>
                <HtmlTagListTab />
              </TabPanel>
              {isCustomHtmlAvailable && (
                <TabPanel forceRender={false}>
                  <AttributesTabContent />
                </TabPanel>
              )}
            </Tabs>
          </div>
        </div>
      </>
    ),
    [selectedNodeId, activeClasses]
  );
}

export default ConfigPanel;
